import { useCallback, useEffect, useRef, useState } from 'react'
import { runTextSearch } from '../../../services/textSearches'
import { EMPTY_RULE } from '../../search'

const DEFAULT_LIMIT = 40

export const EMPTY_DRAFT = {
  findRuleLogic: 'or',
  findRules: [{ ...EMPTY_RULE }],
  exemptRuleLogic: 'or',
  exemptRules: [],
}

export function usePdfSearch(standard, pageRange) {
  // Depend on the range's primitive bounds, not the array identity: callers often
  // pass an inline array (e.g. `pageRange={[start, end]}`) whose reference changes
  // on every parent render, which would otherwise reset the search constantly.
  const start = Array.isArray(pageRange) && pageRange.length === 2 ? pageRange[0] : null
  const end = Array.isArray(pageRange) && pageRange.length === 2 ? pageRange[1] : null
  const range = start != null && end != null ? [start, end] : undefined

  const [draft, setDraft] = useState(EMPTY_DRAFT)
  // The range the last Run used, so Load more stays on that same slice even if
  // the reader scrolls to another page afterward.
  const activeRangeRef = useRef(range)
  const [state, setState] = useState({
    status: 'idle',
    results: [],
    error: '',
    cursor: null,
    hasMore: false,
    loadingMore: false,
  })

  useEffect(() => {
    activeRangeRef.current = range
    setDraft(EMPTY_DRAFT)
    setState({
      status: 'idle',
      results: [],
      error: '',
      cursor: null,
      hasMore: false,
      loadingMore: false,
    })
  }, [standard, start, end])

  const run = useCallback(async (options = {}) => {
    if (!standard) return
    // `pageRange: undefined` means the whole document. Omit the key to keep the
    // viewer's own slice (an item opened on a page range).
    const pageRange = 'pageRange' in options ? options.pageRange : range
    activeRangeRef.current = pageRange
    setState((s) => ({ ...s, status: 'loading', error: '', loadingMore: false }))
    try {
      const out = await runTextSearch({
        standard,
        pageRange,
        findRules: draft.findRules,
        findRuleLogic: draft.findRuleLogic,
        exemptRules: draft.exemptRules,
        exemptRuleLogic: draft.exemptRuleLogic,
        limit: DEFAULT_LIMIT,
      })
      setState({
        status: 'done',
        results: out.results || [],
        error: '',
        cursor: out.nextCursor || null,
        hasMore: out.hasMore || false,
        loadingMore: false,
      })
    } catch (e) {
      setState({
        status: 'error',
        results: [],
        error: e?.message || 'Search failed.',
        cursor: null,
        hasMore: false,
        loadingMore: false,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standard, start, end, draft])

  const loadMore = useCallback(async () => {
    if (!standard || !state.cursor || state.loadingMore) return
    setState((s) => ({ ...s, loadingMore: true }))
    try {
      const out = await runTextSearch({
        standard,
        pageRange: activeRangeRef.current,
        findRules: draft.findRules,
        findRuleLogic: draft.findRuleLogic,
        exemptRules: draft.exemptRules,
        exemptRuleLogic: draft.exemptRuleLogic,
        cursor: state.cursor,
        limit: DEFAULT_LIMIT,
      })
      setState((s) => ({
        ...s,
        results: [...s.results, ...(out.results || [])],
        cursor: out.nextCursor || null,
        hasMore: out.hasMore || false,
        loadingMore: false,
      }))
    } catch (e) {
      setState((s) => ({ ...s, loadingMore: false, error: e?.message || 'Load failed.' }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [standard, start, end, draft, state.cursor, state.loadingMore])

  return { draft, setDraft, state, run, loadMore }
}
